import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/People";
import DownloadIcon from "@material-ui/icons/GetApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListDialog from "../../components/ContactListDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";

import planilhaExemplo from "../../assets/planilha.xlsx";
import { SocketContext } from "../../context/Socket/SocketContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTLISTS") {
    const contactLists = action.payload;
    const newContactLists = [];

    contactLists.forEach((contactList) => {
      const contactListIndex = state.findIndex((u) => u.id === contactList.id);
      if (contactListIndex !== -1) {
        state[contactListIndex] = contactList;
      } else {
        newContactLists.push(contactList);
      }
    });

    return [...state, ...newContactLists];
  }

  if (action.type === "UPDATE_CONTACTLIST") {
    const contactList = action.payload;
    const contactListIndex = state.findIndex((u) => u.id === contactList.id);

    if (contactListIndex !== -1) {
      state[contactListIndex] = contactList;
      return [...state];
    } else {
      return [contactList, ...state];
    }
  }

  if (action.type === "DELETE_CONTACTLIST") {
    const contactListId = action.payload;

    const contactListIndex = state.findIndex((u) => u.id === contactListId);
    if (contactListIndex !== -1) {
      state.splice(contactListIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
      : "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
    minHeight: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.6)
      : alpha("#fff", 0.8),
    backdropFilter: "blur(10px)",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.08)}`,
    borderRadius: "16px 16px 0 0",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      padding: theme.spacing(2),
    },
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: `linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 6px 20px ${alpha("#00bcd4", 0.35)}`,
    "& svg": {
      color: "#fff",
      fontSize: 24,
    },
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.5rem",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #fff 0%, #ccc 100%)"
      : "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  actionsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchField: {
    minWidth: 280,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.background.default, 0.5)
        : "#fff",
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
      },
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
  addButton: {
    borderRadius: 12,
    padding: theme.spacing(1.2, 3),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.95rem",
    background: `linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)`,
    boxShadow: `0 4px 14px ${alpha("#00bcd4", 0.35)}`,
    "&:hover": {
      background: `linear-gradient(135deg, #0097a7 0%, #00838f 100%)`,
      boxShadow: `0 6px 20px ${alpha("#00bcd4", 0.45)}`,
      transform: "translateY(-1px)",
    },
  },
  mainPaper: {
    flex: 1,
    margin: theme.spacing(0, 2, 2, 2),
    borderRadius: 16,
    overflow: "hidden",
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.8)
      : theme.palette.background.paper,
    backdropFilter: "blur(10px)",
    border: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.05)}`,
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
      : "0 4px 20px rgba(0, 0, 0, 0.08)",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.default, 0.5)
      : alpha(theme.palette.grey[100], 0.8),
    "& th": {
      fontWeight: 700,
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      padding: theme.spacing(2),
      whiteSpace: "nowrap",
    },
  },
  tableRow: {
    transition: "all 0.2s ease",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? alpha(theme.palette.primary.main, 0.08)
        : alpha(theme.palette.primary.main, 0.04),
    },
    "& td": {
      padding: theme.spacing(1.5, 2),
      borderBottom: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.06) : alpha("#000", 0.06)}`,
    },
  },
  cellText: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  countChip: {
    fontWeight: 600,
    borderRadius: 8,
    background: alpha(theme.palette.primary.main, 0.15),
    color: theme.palette.primary.main,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    transition: "all 0.2s ease",
    color: theme.palette.text.secondary,
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  viewButton: {
    "&:hover": {
      background: alpha(theme.palette.info.main, 0.15),
      color: theme.palette.info.main,
    },
  },
  downloadButton: {
    "&:hover": {
      background: alpha(theme.palette.success.main, 0.15),
      color: theme.palette.success.main,
    },
  },
  editButton: {
    "&:hover": {
      background: alpha(theme.palette.primary.main, 0.15),
      color: theme.palette.primary.main,
    },
  },
  deleteButton: {
    "&:hover": {
      background: alpha(theme.palette.error.main, 0.15),
      color: theme.palette.error.main,
    },
  },
}));

const ContactLists = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [deletingContactList, setDeletingContactList] = useState(null);
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contactLists, dispatch] = useReducer(reducer, []);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContactLists = async () => {
        try {
          const { data } = await api.get("/contact-lists/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_CONTACTLISTS", payload: data.records });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContactLists();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-ContactList`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTLIST", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACTLIST", payload: +data.id });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  const handleOpenContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(true);
  };

  const handleCloseContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditContactList = (contactList) => {
    setSelectedContactList(contactList);
    setContactListModalOpen(true);
  };

  const handleDeleteContactList = async (contactListId) => {
    try {
      await api.delete(`/contact-lists/${contactListId}`);
      toast.success(i18n.t("contactLists.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContactList(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContacts = (id) => {
    history.push(`/contact-lists/${id}/contacts`);
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <ConfirmationModal
        title={
          deletingContactList &&
          `${i18n.t("contactLists.confirmationModal.deleteTitle")} ${
            deletingContactList.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteContactList(deletingContactList.id)}
      >
        {i18n.t("contactLists.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ContactListDialog
        open={contactListModalOpen}
        onClose={handleCloseContactListModal}
        aria-labelledby="form-dialog-title"
        contactListId={selectedContactList && selectedContactList.id}
      />
      
      {/* Modern Header */}
      <Box className={classes.header}>
        <Box className={classes.titleWrapper}>
          <Box className={classes.titleIcon}>
            <ListAltIcon />
          </Box>
          <Typography className={classes.titleText}>
            {i18n.t("contactLists.title")}
          </Typography>
        </Box>
        <Box className={classes.actionsWrapper}>
          <TextField
            className={classes.searchField}
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={handleOpenContactListModal}
            startIcon={<AddIcon />}
          >
            {i18n.t("contactLists.buttons.add")}
          </Button>
        </Box>
      </Box>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <Table size="small" className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell align="left">
                {i18n.t("contactLists.table.name")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contactLists.table.contacts")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contactLists.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contactLists.map((contactList) => (
                <TableRow key={contactList.id} className={classes.tableRow}>
                  <TableCell align="left">
                    <Typography className={classes.cellText}>
                      {contactList.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={contactList.contactsCount || 0}
                      size="small"
                      className={classes.countChip}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title="Baixar Planilha Exemplo">
                        <a href={planilhaExemplo} download="planilha.xlsx">
                          <IconButton
                            size="small"
                            className={`${classes.actionButton} ${classes.downloadButton}`}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </a>
                      </Tooltip>

                      <Tooltip title="Ver Contatos">
                        <IconButton
                          size="small"
                          onClick={() => goToContacts(contactList.id)}
                          className={`${classes.actionButton} ${classes.viewButton}`}
                        >
                          <PeopleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEditContactList(contactList)}
                          className={`${classes.actionButton} ${classes.editButton}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setConfirmModalOpen(true);
                            setDeletingContactList(contactList);
                          }}
                          className={`${classes.actionButton} ${classes.deleteButton}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={3} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ContactLists;
